import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { services } from '@services/api';

interface GroupSelectionDialogProps {
  open: boolean;
  onComplete: () => void;
}

const GroupSelectionDialog: React.FC<GroupSelectionDialogProps> = ({ open, onComplete }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const { data: ec2Tags, isLoading: ec2Loading } = useQuery<string[]>({
    queryKey: ['ec2-product-tags'],
    queryFn: async () => {
      const res = await services.ec2.getProductTags();
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: open,
  });

  const { data: rdsTags, isLoading: rdsLoading } = useQuery<string[]>({
    queryKey: ['rds-product-tags'],
    queryFn: async () => {
      const res = await services.rds.getProductTags();
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: open,
  });

  const isLoading = ec2Loading || rdsLoading;

  const allTags = useMemo(() => {
    const merged = new Set<string>([
      ...(ec2Tags || []),
      ...(rdsTags || []),
    ]);
    return Array.from(merged).sort();
  }, [ec2Tags, rdsTags]);

  const mutation = useMutation({
    mutationFn: (groupNames: string[]) => services.auth.selfAssignGroups(groupNames),
    onSuccess: () => {
      onComplete();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to assign groups. Please try again.');
    },
  });

  const handleToggle = (tag: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    setError(null);
    mutation.mutate(Array.from(selected));
  };

  const noTagsFound = !isLoading && allTags.length === 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>Select Your Groups</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please select the product groups you are associated with. This helps us show you the most relevant resources.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : noTagsFound ? (
          <Alert severity="info">
            No product groups are available at this time. You can skip this step and groups can be assigned by an administrator later.
          </Alert>
        ) : (
          <FormGroup>
            {allTags.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={selected.has(tag)}
                    onChange={() => handleToggle(tag)}
                  />
                }
                label={tag}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        {noTagsFound ? (
          <Button onClick={onComplete} variant="outlined">
            Skip
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={selected.size === 0 || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {mutation.isPending ? 'Saving...' : 'Confirm Selection'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GroupSelectionDialog;
